/**
 * URL parsing and sessionStorage helpers for workspace initialization.
 *
 * All functions are pure (or operate on an injected Storage interface),
 * making them easy to unit test without React or DOM dependencies.
 */

import type { WorkspaceEntry } from './types'
import type { WorkspaceInviteV2 } from '@secureblob/sdk'
import { decodeWorkspaceSecretV2, parseInviteInput } from '@secureblob/sdk/workspaceSecretV2'
import { API_BASE_URL } from '../config'

// ── SessionStorage keys ──────────────────────────────────────────
export const SESSION_KEY_ACTIVE_WORKSPACE = 'sb:active-workspace'
export const SESSION_KEY_GUEST_ACTIVE = 'sb:guest-active'
export const SESSION_KEY_PENDING_WORKSPACE = 'sb:pending-workspace'
const GUEST_CAP_PREFIX = 'sb:guest-cap:'

// ── Hash parse result ────────────────────────────────────────────

export interface HashParseResult {
    /** Parsed workspace entry from hash, or null if no valid hash. */
    entry: WorkspaceEntry | null
    /** Initial file path from `path=` parameter (e.g. `/notes.md`). */
    initialPath: string | null
    /** Initial view mode from `view=` parameter (`'edit'` or `'preview'`), or null. */
    initialViewMode: 'edit' | 'preview' | null
    /** Human-readable error message, or null if no error. */
    error: string | null
    /** Identity import payload from `#identity=...`, or null. */
    identityPayload: string | null
    /**
     * Parsed v2 invite when hash contains `#invite=...` or `sb-invite:...`.
     * Spec §4.2 / §8.3: invite links carry the full WorkspaceInviteV2
     * envelope including embedded workspace secret and server-signed token.
     * Null for legacy capability links and identity imports.
     */
    inviteV2: WorkspaceInviteV2 | null
}

function normalizeShareLinkPath(rawPath: string | null): string | null {
    if (!rawPath) return null

    // Match SDK path invariants: POSIX separators + no traversal segments.
    let normalized = rawPath.replace(/\\/g, '/').replace(/\/+/g, '/')
    if (!normalized.startsWith('/')) normalized = `/${normalized}`
    if (normalized.length > 1 && normalized.endsWith('/')) normalized = normalized.slice(0, -1)

    const segments = normalized.slice(1).split('/').filter(Boolean)
    if (segments.some((segment) => segment === '.' || segment === '..')) {
        return null
    }

    return normalized
}

/**
 * Parse `location.hash` into a workspace entry + optional path.
 *
 * Supports:
 * - `#invite=<base64url(WorkspaceInviteV2)>` (v2 invite link, Spec §4.2)
 * - `#blobId&k=capability&w=1&path=/notes.md&view=preview&title=X&emoji=🔗`
 * - `#identity=sb-key:...` (identity import — legacy, being removed)
 */
export function parseWorkspaceFromHash(hash: string): HashParseResult {
    const trimmed = hash.startsWith('#') ? hash.slice(1) : hash
    const empty = { entry: null, initialPath: null, initialViewMode: null, error: null, identityPayload: null, inviteV2: null } as HashParseResult
    if (!trimmed) return empty

    // ── v2 invite: #invite=<...> or sb-invite:<...> ───────────────
    const inviteLike = trimmed.startsWith('invite=') || trimmed.startsWith('sb-invite:')
    if (inviteLike) {
        const invite = parseInviteInput(trimmed.startsWith('invite=') ? `#${trimmed}` : trimmed)
        if (!invite) {
            return { ...empty, error: 'Invalid link: could not decode v2 invite.' }
        }

        const secret = decodeWorkspaceSecretV2(invite.secret)
        if (!secret) {
            return { ...empty, error: 'Invalid link: invite secret is malformed.' }
        }

        const entry: WorkspaceEntry = {
            blobId: secret.blobId,
            capability: invite.secret,
            apiBaseUrl: invite.apiBaseUrl,
            label: invite.title || 'Shared workspace',
            emoji: invite.emoji || '🔗',
            canWrite: invite.mode === 'write',
        }

        return {
            entry,
            inviteV2: invite,
            initialPath: normalizeShareLinkPath(invite.path ?? null),
            initialViewMode: (invite.view === 'edit' || invite.view === 'preview') ? invite.view : null,
            error: null,
            identityPayload: null,
        }
    }

    // Detect identity import links: #identity=sb-key:...
    if (trimmed.startsWith('identity=')) {
        try {
            const payload = decodeURIComponent(trimmed.slice('identity='.length))
            return { entry: null, initialPath: null, initialViewMode: null, error: null, identityPayload: payload, inviteV2: null }
        } catch {
            return { entry: null, initialPath: null, initialViewMode: null, error: 'Invalid link: malformed identity payload.', identityPayload: null, inviteV2: null }
        }
    }

    const [blobIdPart, ...queryParts] = trimmed.split('&')
    let blobId = ''
    try {
        blobId = decodeURIComponent(blobIdPart ?? '').trim()
    } catch {
        return { entry: null, initialPath: null, initialViewMode: null, error: 'Invalid link: malformed blob id.', identityPayload: null, inviteV2: null }
    }
    if (!blobId) return { entry: null, initialPath: null, initialViewMode: null, error: 'Invalid link: missing blob id.', identityPayload: null, inviteV2: null }

    const params = new URLSearchParams(queryParts.filter(Boolean).join('&'))
    const capability = params.get('k')
    if (!capability) {
        return { entry: null, initialPath: null, initialViewMode: null, error: 'Invalid link: missing capability key.', identityPayload: null, inviteV2: null }
    }

    const normalizedPath = normalizeShareLinkPath(params.get('path'))

    const rawView = params.get('view')
    const initialViewMode: 'edit' | 'preview' | null =
        rawView === 'edit' || rawView === 'preview' ? rawView : null

    return {
        entry: {
            blobId,
            capability,
            apiBaseUrl: API_BASE_URL,
            label: params.get('title') || `Shared ${blobId}`,
            emoji: params.get('emoji') || '🔗',
            canWrite: params.get('w') === '1',
        },
        initialPath: normalizedPath,
        initialViewMode,
        error: null,
        identityPayload: null,
        inviteV2: null,
    }
}

// ── Guest capability session persistence ─────────────────────────

export interface GuestSession {
    blobId: string
    capability: string
    canWrite: boolean
    label: string
    emoji: string
    apiBaseUrl: string
}

/**
 * Save guest workspace capability to sessionStorage (per-tab).
 * Called when opening a share link without a profile.
 */
export function saveGuestCapToSession(storage: Storage, entry: WorkspaceEntry): void {
    const data: GuestSession = {
        blobId: entry.blobId,
        capability: entry.capability,
        canWrite: entry.canWrite ?? false,
        label: entry.label,
        emoji: entry.emoji,
        apiBaseUrl: entry.apiBaseUrl,
    }
    storage.setItem(`${GUEST_CAP_PREFIX}${entry.blobId}`, JSON.stringify(data))
    storage.setItem(SESSION_KEY_GUEST_ACTIVE, entry.blobId)
}

/**
 * Read guest workspace capability from sessionStorage.
 * Returns null if no guest session exists in this tab.
 */
export function getGuestCapFromSession(storage: Storage): WorkspaceEntry | null {
    const activeBlobId = storage.getItem(SESSION_KEY_GUEST_ACTIVE)
    if (!activeBlobId) return null

    const raw = storage.getItem(`${GUEST_CAP_PREFIX}${activeBlobId}`)
    if (!raw) {
        // Defensive cleanup: prevent stale share-link mode if active pointer survives
        // while the capability payload has been removed/corrupted.
        storage.removeItem(SESSION_KEY_GUEST_ACTIVE)
        return null
    }

    try {
        const data: GuestSession = JSON.parse(raw)
        return {
            blobId: data.blobId,
            capability: data.capability,
            canWrite: data.canWrite,
            label: data.label,
            emoji: data.emoji,
            apiBaseUrl: data.apiBaseUrl,
        }
    } catch {
        // Defensive cleanup: malformed payload should not keep the tab in
        // share-link entry mode for subsequent loads.
        storage.removeItem(`${GUEST_CAP_PREFIX}${activeBlobId}`)
        storage.removeItem(SESSION_KEY_GUEST_ACTIVE)
        return null
    }
}

/**
 * Clear all guest session data from sessionStorage.
 */
export function clearGuestSession(storage: Storage): void {
    const blobId = storage.getItem(SESSION_KEY_GUEST_ACTIVE)
    if (blobId) {
        storage.removeItem(`${GUEST_CAP_PREFIX}${blobId}`)
    }
    storage.removeItem(SESSION_KEY_GUEST_ACTIVE)
}

// ── Active workspace session persistence ─────────────────────────

/**
 * Save active workspace blobId to sessionStorage (per-tab).
 * This ensures F5 reopens the same workspace, not the registry's lastOpenedAt.
 */
export function saveActiveWorkspace(storage: Storage, blobId: string): void {
    storage.setItem(SESSION_KEY_ACTIVE_WORKSPACE, blobId)
}

/**
 * Read active workspace blobId from sessionStorage.
 * Returns null if no active workspace is saved for this tab.
 */
export function getActiveWorkspace(storage: Storage): string | null {
    return storage.getItem(SESSION_KEY_ACTIVE_WORKSPACE) || null
}

/**
 * Clear active workspace from sessionStorage.
 */
export function clearActiveWorkspace(storage: Storage): void {
    storage.removeItem(SESSION_KEY_ACTIVE_WORKSPACE)
}

// ── Pending workspace (guest → profile transition) ───────────────

/**
 * Save a pending workspace entry so it can be auto-added to the registry
 * when the guest creates a profile. Called when opening a share link.
 */
export function savePendingWorkspace(storage: Storage, entry: WorkspaceEntry): void {
    const data: GuestSession = {
        blobId: entry.blobId,
        capability: entry.capability,
        canWrite: entry.canWrite ?? false,
        label: entry.label,
        emoji: entry.emoji,
        apiBaseUrl: entry.apiBaseUrl,
    }
    storage.setItem(SESSION_KEY_PENDING_WORKSPACE, JSON.stringify(data))
}

/**
 * Read and return the pending workspace entry, or null if none exists.
 */
export function getPendingWorkspace(storage: Storage): WorkspaceEntry | null {
    const raw = storage.getItem(SESSION_KEY_PENDING_WORKSPACE)
    if (!raw) return null

    try {
        const data: GuestSession = JSON.parse(raw)
        return {
            blobId: data.blobId,
            capability: data.capability,
            canWrite: data.canWrite,
            label: data.label,
            emoji: data.emoji,
            apiBaseUrl: data.apiBaseUrl,
        }
    } catch {
        return null
    }
}

/**
 * Clear the pending workspace entry after it has been consumed.
 */
export function clearPendingWorkspace(storage: Storage): void {
    storage.removeItem(SESSION_KEY_PENDING_WORKSPACE)
}
